import React from "react";
import tw from "twin.macro";
import Header from "../headers/light.js";

const Container = tw.div`relative mt-6`;
const SingleColumn = tw.div`max-w-screen-xl mx-auto py-24`;
const SectionHead = tw.h2`text-4xl sm:text-4xl font-black tracking-wide`;
const HeadingInfoContainer = tw.div`flex flex-col`;
const HeadingDescription = tw.p`mt-5 font-medium text-gray-600 max-w-2xl leading-relaxed`;
const Content = tw.div`mt-12 space-y-12 text-left max-w-3xl`;
const SectionTitle = tw.h4`text-2xl font-bold text-gray-900`;
const Paragraph = tw.p`mt-2 text-gray-700 leading-relaxed`;
const List = tw.ul`list-disc list-inside mt-2 space-y-1 text-gray-700`;
const SubList = tw.ul`list-decimal list-inside ml-5 mt-2 space-y-1 text-gray-700`;

export default function PrivacyPolicy() {
  return (
    <>
      <Header roundedHeaderButton={false} />
      <Container>
        <SingleColumn>
          <HeadingInfoContainer>
            <SectionHead>Privacy Policy for Tabebek EHR</SectionHead>
            <HeadingDescription>
              This Privacy Policy explains how Tabebek EHR collects, uses, and
              protects information when you use our services and applications.
            </HeadingDescription>
          </HeadingInfoContainer>

          <Content>
            {/* 1 */}
            <div>
              <SectionTitle>1. Overview</SectionTitle>
              <Paragraph>
                This Privacy Policy explains how Tabebek EHR ("Tabebek EHR,"
                "we," or "our") collects, uses, and protects information when
                you use our website, our web-based clinical (EHR) solutions and
                services ("Services"), and mobile applications. This policy
                applies to all visitors and registered users. We are committed
                to ensuring your privacy is protected and handling your data in
                accordance with applicable laws and best industry practices.
              </Paragraph>
            </div>

            {/* 2 */}
            <div>
              <SectionTitle>2. Purpose and Scope</SectionTitle>
              <Paragraph>
                This Privacy Policy is designed to inform you about the
                information we gather, how we use it, and your rights regarding
                your personal data. For Tabebek EHR’s clients, additional
                details may be outlined in contracts.
              </Paragraph>
              <List>
                <li>Through our Website</li>
                <li>Mobile application</li>
                <li>Via email, text, and other electronic communications</li>
              </List>
            </div>

            {/* 3 */}
            <div>
              <SectionTitle>3. Information We Collect</SectionTitle>
              <Paragraph>We collect various types of information:</Paragraph>
              <List>
                <li>
                  <strong>Personally Identifiable Information:</strong> Name,
                  email, address, phone number, etc.
                </li>
                <li>
                  <strong>User Data:</strong> IP address, device type, browser
                  details, pages visited, and time spent.
                </li>
                <li>
                  <strong>Cookies and Tracking:</strong> Information gathered
                  through cookies, web beacons, and similar tools.
                </li>
              </List>
              <Paragraph>We collect information in the following ways:</Paragraph>
              <List>
                <li>Directly from you (e.g., registration, contact forms).</li>
                <li>
                  Automatically, through technologies such as cookies or web
                  beacons.
                </li>
                <li>From third parties, such as social media or partners.</li>
              </List>
            </div>

            {/* 4 */}
            <div>
              <SectionTitle>4. How We Use Your Information</SectionTitle>
              <List>
                <li>To deliver the Services you request.</li>
                <li>To enhance and personalize your experience.</li>
                <li>To communicate with you about updates.</li>
                <li>To process transactions and fulfill obligations.</li>
                <li>To provide customer support.</li>
                <li>To comply with legal requirements.</li>
              </List>
            </div>

            {/* 5 */}
            <div>
              <SectionTitle>5. Sharing Your Information</SectionTitle>
              <Paragraph>
                We may share aggregated, non-identifiable information freely.
                We may disclose your data in cases such as:
              </Paragraph>
              <List>
                <li>As required by law or legal processes.</li>
                <li>
                  To protect our rights, safety, or the safety of others in
                  emergencies.
                </li>
                <li>During mergers, acquisitions, or asset sales.</li>
                <li>With affiliates or service providers.</li>
              </List>
              <Paragraph>
                <strong>We do not sell, rent, or trade your personal data.</strong>
              </Paragraph>
            </div>

            {/* 6 */}
            <div>
              <SectionTitle>6. Data Protection</SectionTitle>
              <Paragraph>
                We implement reasonable measures to secure your data but no
                system can guarantee complete security.
              </Paragraph>
              <Paragraph>Types of cookies used:</Paragraph>
              <List>
                <li>Essential cookies (basic site functions)</li>
                <li>Functional cookies (enhance browsing)</li>
                <li>Advertising cookies (targeted ads)</li>
              </List>
              <Paragraph>
                You may modify browser settings to reject cookies, but this may
                affect your experience.
              </Paragraph>
            </div>

            {/* 7 */}
            <div>
              <SectionTitle>7. Your Rights and Choices</SectionTitle>
              <Paragraph>
                Under applicable data protection laws, you have the following
                rights:
              </Paragraph>
              <List>
                <li>Access – request a copy of your data.</li>
                <li>Correction – fix inaccurate info.</li>
                <li>Erasure – request deletion of your data.</li>
                <li>
                  Restriction – limit processing in specific situations.
                </li>
                <li>
                  Data Portability – transfer data in a machine-readable format.
                </li>
                <li>Objection – object to processing or marketing.</li>
              </List>
              <Paragraph>
                To exercise these rights, contact us at{" "}
                <span className="font-semibold">care@tabebek.com</span>.
              </Paragraph>
            </div>

            {/* 8 */}
            <div>
              <SectionTitle>8. Data Retention</SectionTitle>
              <Paragraph>
                We retain personal data only as long as necessary to fulfill the
                purposes outlined in this Privacy Policy and comply with legal
                obligations.
              </Paragraph>
            </div>

            {/* 9 */}
            <div>
              <SectionTitle>9. Children’s Privacy</SectionTitle>
              <Paragraph>
                Our Website and Services are not intended for individuals under
                16. We do not knowingly collect their data. If you believe we
                have, please contact us immediately.
              </Paragraph>
            </div>

            {/* 10 */}
            <div>
              <SectionTitle>10. Changes to This Privacy Policy</SectionTitle>
              <Paragraph>
                We may update this policy from time to time. Updates will appear
                here with a revised “Last Revised” date. Continued use indicates
                acceptance.
              </Paragraph>
            </div>

            {/* 11 */}
            <div>
              <SectionTitle>11. Account Deletion Request</SectionTitle>
              <Paragraph>
                To delete your account, email{" "}
                <span className="font-semibold">care@tabebek.com</span> with the
                subject line <em>“Account Deletion Request.”</em>
              </Paragraph>
            </div>
          </Content>
        </SingleColumn>
      </Container>
    </>
  );
}
